import React, { Component } from 'react';
import { workoutProgramWithDescriptions } from 'fitbeat.gym-core';
import { IEnabledFeatures, ProgramType } from 'fitbeat.models';
import { Text, View, StyleSheet, ScrollView, ActivityIndicator } from 'react-native';
import { Button, ButtonTypes } from '../../components';
import { FormError, BuildIcon } from '../../ui';
import Theme from '../../theme';
import FastImage from 'react-native-fast-image';
import { BookingFlowFooter } from '../../Bookings';
import { connect } from 'react-redux';
import { enabledFeaturesSelector } from '../../auth';
import { IAppStore } from '../../HomeWorkouts';
import { TranslateProp } from '../../common-types';

interface IProps {
  t: TranslateProp;
  isLoading: boolean;
  longTermGoal: string;
  back: () => void;
  selectGoal: () => void;
  error?: Error;
  enabledFeatures: IEnabledFeatures;
}

interface IState {
  programGoalWithDescription: {
    programType: string;
    workoutDescription: string;
    nutritionDescription: string;
  };
}

class WorkoutProgramDescriptionDisplay extends Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);
    const { enabledFeatures } = props;

    const programGoalWithDescription = workoutProgramWithDescriptions(enabledFeatures)
      .find((programDescriptions) =>
        programDescriptions.programType === props.longTermGoal) || {
          programType: ProgramType.NotSet,
          workoutDescription: '',
          nutritionDescription: '',
        };

    this.state = {
      programGoalWithDescription,
    };
  }

  public render() {
    const { t, error, isLoading, back, selectGoal, enabledFeatures } = this.props;
    const { programGoalWithDescription } = this.state;

    if (isLoading) {
      return (
        <View style={styles.loadingContainer}>
          <ActivityIndicator
            size='small'
            color={Theme.PRIMARY}
            animating={isLoading}
            style={styles.activityIndicator}
          />
        </View>
      );
    }

    const buttonTitle = programGoalWithDescription.programType === ProgramType.NotSet
      ? t('workoutGoals:continue')
      : t('workoutGoals:makeThisMyGoal');

    return (
      <View style={styles.container}>
        <ScrollView style={styles.container}>
          <View style={[styles.goalDescriptions, styles.borderBottom]}>
            <View style={styles.iconContainer}>
              <BuildIcon
                width={35}
                height={35}
                color={Theme.BLACK}
                styles={{
                  transform: [{ rotate: '-45deg' }],
                }}
              />
            </View>
            <View style={styles.descriptionContainer}>
              <Text style={styles.header}>Workouts</Text>
              <Text style={styles.description}>
                {programGoalWithDescription.workoutDescription}
              </Text>
            </View>
          </View>

          <View style={styles.goalDescriptions}>
            <View style={styles.iconContainer}>
              <FastImage
                resizeMode='contain'
                style={{
                  width: 40,
                  height: 35,
                }}
                source={require('../../assets/images/icon-nutrition-black.png')}
              />
            </View>

            <View style={styles.descriptionContainer}>
              <Text style={styles.header}>Nutrition</Text>
              <Text style={styles.description}>
                {programGoalWithDescription.nutritionDescription}
              </Text>
            </View>
          </View>
        </ScrollView>

        <BookingFlowFooter>
          <FormError error={error} />
          <View style={styles.buttonContainer}>
            <Button
              title={t('back')}
              fontColor={Theme.PRIMARY}
              buttonType={ButtonTypes.OUTLINE}
              onPress={back}
              disabled={isLoading}
              containerStyle={styles.backButton}
            />
            <Button
              title={buttonTitle}
              fontColor={Theme.WHITE}
              onPress={selectGoal}
              disabled={isLoading}
              containerStyle={styles.selectButton}
            />
          </View>
        </BookingFlowFooter>
      </View>
    );
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  activityIndicator: {
    flex: 1,
    alignSelf: 'center',
  },
  buttonContainer: {
    flexDirection: 'row',
  },
  backButton: {
    flex: 0.3,
    marginRight: 5,
  },
  selectButton: {
    flex: 0.7,
  },
  goalDescriptions: {
    flexDirection: 'row',
    paddingVertical: 20,
  },
  iconContainer: {
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: Theme.GREY_LIGHT,
    justifyContent: 'center',
    alignItems: 'center',
  },
  descriptionContainer: {
    flex: 1,
    paddingLeft: 10,
  },
  header: {
    fontSize: 20,
    paddingVertical: 10,
    color: Theme.CHARCOAL,
    fontFamily: Theme.Custom800,
  },
  description: {
    fontSize: 16,
    color: Theme.CHARCOAL,
    fontFamily: Theme.Custom500,
  },
  borderBottom: {
    borderBottomWidth: 2,
    borderBottomColor: Theme.GREY_LIGHT,
  },
});

const mapStateToProps = (state: IAppStore) => ({
  enabledFeatures: enabledFeaturesSelector(state),
});

export default connect(mapStateToProps)(WorkoutProgramDescriptionDisplay);
